import React, {useEffect, useState} from 'react';
import {getUserFriendlyErrorMessage} from '@/utils/errorHandling.ts';
import {
  Button,
  EmptyState,
  ErrorHappened,
  HeadingSize,
  Label,
  LabelType,
  Loading,
  NoResultsFound,
  Search,
  SorterResult,
  Table,
  useKeyboardShortcut
} from '@fs/uikit-react';
import styles from './PersonsPage.module.css';
import {ApiError} from '@/services/api';
import { refreshPersons, useInit } from '../../hooks/useInit.ts';
import {
  filteredPersons,
  getRowActions,
  getTableColumns,
  handleCreatePerson,
  handleDeletePerson
} from './table.tsx';
import { Person } from '../../types/person.ts';
import { useNavigate } from 'react-router-dom';

const PersonsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');

  const [persons, setPersons] = useState<Person[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [sorters, setSorters] = useState<SorterResult[]>([]);

  useInit(setPersons, setLoading, setError);

  useEffect(() => {
    refreshPersons();
  }, []);

  useKeyboardShortcut('c', [], handleCreatePerson);

  const filtered = filteredPersons(persons, searchValue);

  const renderHeader = () => {
    return (
      <div className={styles.title}>
        <Label
          text={'Особи'}
          type={LabelType.HEADING}
          headingSize={HeadingSize.L}
        />
        <div className={styles.buttonContainer}>
          <Button
            onClick={handleCreatePerson}
            title="Створити особу"
          />
        </div>
      </div>
    );
  };

  const renderSearch = () => {
    return (
      <div className={styles.searchButtonContainer}>
        <div className={styles.searchContainer}>
          <Search
            placeholder="Пошук осіб"
            value={searchValue}
            onChange={setSearchValue}
          />
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <Loading text="Загружаємо осіб..." className={styles.loadingState} />;
    }

    if (error) {
      return <ErrorHappened text={getUserFriendlyErrorMessage(error as ApiError)} />;
    }

    if (filtered.length === 0) {
      if (searchValue) {
        return (
          <NoResultsFound
            text={`Немає результатів за запитом "${searchValue}"`}
          />
        );
      } else {
        return <EmptyState text="Немає доступних осіб" />;
      }
    }

    return (
      <Table
        data={filtered || []}
        columns={getTableColumns()}
        rowActions={getRowActions(handleDeletePerson)}
        onClick={(record) => {
          const person = record as Person;
          if (person?.id) {
            navigate(`/operator/person/${person.id}`);
          }
        }}
        onSort={setSorters}
        initialSorters={sorters}
        enablePagination={filtered.length > 10}
        paginationConfig={{
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          showSizeChanger: true
        }}
      />
    );
  };

  return (
    <div className={styles.container}>
      {renderHeader()}
      <div>
        {renderSearch()}
        {renderContent()}
      </div>
    </div>
  );
};

export default PersonsPage;
